import type { ChunkSet } from '../../gen/rooms';

/**
 * Hand-made room chunks for the Moon (GDD: each planet adds one movement idea; the Moon's is low
 * gravity). A jump rises about 5 tiles here instead of 3 and carries about 8 tiles across, so the
 * gaps are wider and the ledges higher than the jungle's; a low ceiling (the old base) punishes
 * the float. Same format and seam rule as the jungle chunks:
 * `.` empty · `#` solid · `=` one-way · `P` start · `*` Starlight cell · `m` material shard ·
 * `c` crawler · `d` drone · `X` exit · `~` radiation · `w` weapon mod cache. Shards sit in the
 * hard-to-reach spots.
 */
const CHUNKS = {
  start: {
    id: 'm-start',
    // The landing site: flat ground, a girder up high to try the first moon jump on.
    lanes: [
      [
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '..............*.*.......',
        '.............=====......',
        '........................',
        '........................',
        '........................',
        '........................',
        '...P....................',
        '########################',
        '########################',
        '########################',
      ],
      [
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '................*.......',
        '..............=====.....',
        '........................',
        '........................',
        '########################',
        '########################',
        '########################',
      ],
      [
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '................*.......',
        '........................',
        '########################',
        '########################',
        '########################',
      ],
    ],
  },
  craters: {
    id: 'craters',
    // Two crater bowls, then a mesa four tiles tall: one moon jump clears it.
    lanes: [
      [
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '..............**........',
        '........................',
        '.............######.....',
        '.............######.....',
        '.............######.....',
        '.............######.....',
        '####....###.############',
        '####....###.############',
        '########################',
      ],
      [
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '.........c..............',
        '########################',
        '########################',
        '########################',
      ],
      [
        '........................',
        '........................',
        '...........m............',
        '..........===...........',
        '........................',
        '........................',
        '........................',
        '.....=====..............',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '########################',
        '########################',
        '########################',
      ],
    ],
  },
  towers: {
    id: 'towers',
    // Rock pillars rising 3, 6 and 9 tiles: hop from one to the next.
    lanes: [
      [
        '........................',
        '........................',
        '................m.......',
        '................*.......',
        '........................',
        '...............###......',
        '...............###......',
        '...............###......',
        '..........##...###......',
        '..........##...###......',
        '..........##...###......',
        '.....##...##...###......',
        '.....##...##...###......',
        '.....##...##...###......',
        '########################',
        '########################',
        '########################',
      ],
      [
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '............d...........',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '.......*................',
        '........................',
        '########################',
        '########################',
        '########################',
      ],
      [
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '..............*.........',
        '........................',
        '.........c..............',
        '########################',
        '########################',
        '########################',
      ],
    ],
  },
  chasm: {
    id: 'chasm',
    // A twelve-tile chasm in the near lane with two floating rocks to land on; a narrower one
    // behind it.
    lanes: [
      [
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '...........*............',
        '........................',
        '..........===...........',
        '........................',
        '.......===......===.....',
        '........................',
        '........................',
        '######............######',
        '######............######',
        '######............######',
      ],
      [
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '...........d............',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '#########......#########',
        '#########......#########',
        '#########......#########',
      ],
      [
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '...........*............',
        '........................',
        '.....c..........c.......',
        '########################',
        '########################',
        '########################',
      ],
    ],
  },
  base: {
    id: 'base',
    // An abandoned moon base: a low corridor (a full moon jump hits the ceiling) with a crawler
    // inside, a walkway on the roof behind, and a shard in the antenna.
    lanes: [
      [
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '..............m.........',
        '.............===........',
        '........................',
        '....################....',
        '....################....',
        '........................',
        '..........*..*..........',
        '........................',
        '.............c..........',
        '########################',
        '########################',
        '########################',
      ],
      [
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '..........d.............',
        '........................',
        '............w...........',
        '.....==============.....',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '########################',
        '########################',
        '########################',
      ],
      [
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '..........*.............',
        '........................',
        '########################',
        '########################',
        '########################',
      ],
    ],
  },
  drift: {
    id: 'drift',
    // Drifting rocks over a long gap in the near lane, crossed in moon jumps; drones behind.
    lanes: [
      [
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '..........*.............',
        '.........===............',
        '........................',
        '.....===.......===......',
        '........................',
        '........................',
        '####................####',
        '####................####',
        '####................####',
      ],
      [
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '.......d........d.......',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '########################',
        '########################',
        '########################',
      ],
      [
        '........................',
        '...............m........',
        '..............===.......',
        '........................',
        '........................',
        '........................',
        '........................',
        '.........===............',
        '........................',
        '........................',
        '........................',
        '....===.................',
        '........................',
        '........................',
        '########################',
        '########################',
        '########################',
      ],
    ],
  },
  rille: {
    id: 'rille',
    // A plateau to climb in the near lane, a trench of crawlers behind it.
    lanes: [
      [
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '...........**...........',
        '........................',
        '.........######.........',
        '.........######.........',
        '......############......',
        '......############......',
        '......############......',
        '########################',
        '########################',
        '########################',
      ],
      [
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '...........*............',
        '........................',
        '#####...c......c....####',
        '#####...............####',
        '########################',
      ],
      [
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '..........d.............',
        '########################',
        '########################',
        '########################',
      ],
    ],
  },
  reactor: {
    id: 'reactor',
    // A crashed lander's reactor leaks into a crater in the far lane (radiation tier 2): a mod
    // cache on the core and a shard at the bottom, for a plate or two (or Shielding 2).
    lanes: [
      [
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '...........*............',
        '.........#####..........',
        '.........#####...c......',
        '########################',
        '########################',
        '########################',
      ],
      [
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '............d...........',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '.....*..................',
        '########################',
        '########################',
        '########################',
      ],
      [
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '........................',
        '......~~~~~~~~~~~~......',
        '......~~~~~~~~~~~~......',
        '......~~~~~w~~~~~~......',
        '......~~~~~##~~~~~......',
        '######~~~~~##~~~~~######',
        '######~~m~~##~~~*~######',
        '########################',
      ],
    ],
  },
  exit: {
    id: 'm-exit',
    // The way out: an airlock door in every lane, against the end wall.
    lanes: [
      [
        '........................',
        '........................',
        '........................',
        '........................',
        '......................##',
        '......................##',
        '......................##',
        '......................##',
        '......................##',
        '......................##',
        '....................XX##',
        '....................XX##',
        '........*...........XX##',
        '....................XX##',
        '########################',
        '########################',
        '########################',
      ],
      [
        '........................',
        '........................',
        '........................',
        '........................',
        '......................##',
        '......................##',
        '......................##',
        '......................##',
        '......................##',
        '......................##',
        '....................XX##',
        '....................XX##',
        '....................XX##',
        '....................XX##',
        '########################',
        '########################',
        '########################',
      ],
      [
        '........................',
        '........................',
        '........................',
        '........................',
        '......................##',
        '......................##',
        '......................##',
        '......................##',
        '......................##',
        '......................##',
        '....................XX##',
        '....................XX##',
        '....................XX##',
        '....................XX##',
        '########################',
        '########################',
        '########################',
      ],
    ],
  },
} as const;

export const MOON_CHUNKS: ChunkSet = {
  start: CHUNKS.start,
  middle: [
    CHUNKS.craters,
    CHUNKS.towers,
    CHUNKS.chasm,
    CHUNKS.base,
    CHUNKS.drift,
    CHUNKS.rille,
    CHUNKS.reactor,
  ],
  exit: CHUNKS.exit,
};
